// Deterministic Logic-host election (docs/multiplayer-sync/roadmap.md
// section 3.2 item 1): every connected client independently computes the
// same ordering over the relay's own presence list and treats the first
// entry as host - a listen-server pattern (an ordinary player's own client
// stands in for a server), not a new elected-leader protocol. `playerId`
// alone is sufficient for a total order with no ties, since it is already
// unique per player (part of every `RelayPresenceEntry`) - no need for a
// join-time timestamp too (roadmap 3.2 item 1 explains why one is not
// available to clients without a relay change this design does not
// otherwise need).
//
// Re-run on every presence change (a join, a leave, or the host itself
// disconnecting) - migration is exactly "everyone re-runs this and
// converges on the same new host", no separate election protocol needed
// (roadmap 3.2 item 4).

/** The minimal presence-entry shape this needs - a `RelayPresenceEntry`
 * (useRelayConnection.ts) satisfies it directly. */
export interface ElectablePresence {
  playerId: string;
}

/**
 * The current session's Logic host: the lexicographically-smallest
 * `playerId` among `presence`, or `null` for an empty list (no relay
 * connection, or a presence update that raced ahead of the local player's
 * own entry appearing). Pure and total: the same `presence` array (in any
 * order - sorted here, not assumed pre-sorted) always elects the same
 * host, which is the whole point - every client reaches this independently
 * and must agree without talking to each other.
 */
export function electHost(presence: readonly ElectablePresence[]): string | null {
  if (presence.length === 0) return null;
  let host = presence[0]!.playerId;
  for (let i = 1; i < presence.length; i++) {
    const candidate = presence[i]!.playerId;
    if (candidate < host) host = candidate;
  }
  return host;
}

/** True when `localPlayerId` is the elected host of `presence` - the
 * common call shape a session actually needs (roadmap 3.2 items 2/3: what
 * the host does that others don't). A `localPlayerId` of `undefined`
 * (single-player/no-relay session, or a relay connection not yet
 * identified) is never host of anything - see this function's own
 * `electHost(presence).length === 0` case for the mirror image: no
 * presence at all also elects nobody. */
export function isElectedHost(presence: readonly ElectablePresence[], localPlayerId: string | undefined): boolean {
  if (!localPlayerId) return false;
  return electHost(presence) === localPlayerId;
}
